/** 작업지시 창 · QR 패널 화면(폰) */
import React, { useEffect, useState } from 'react';
import { OrderItem, WorkDetail, workDetail, issueOrder, panelAction, errMsg } from '../utils/api';
import { PANEL, jobOf, d8 } from './WorkBoard';
import { overlay, box, head, xBtn, inp, btn, label } from './ui';

const today = () => new Date(Date.now() + 9 * 3600e3).toISOString().slice(0, 10);
const input: React.CSSProperties = { ...inp, width: '100%', marginTop: 6 };

/** 작업지시: 저장된 도면의 패널 목록으로 패널 행을 만들고 작업 중으로 */
export const WorkOrderDialog: React.FC<{ estiNo: string; items: OrderItem[]; onDone: (msg: string) => void; onClose: () => void }> = ({ estiNo, items, onDone, onClose }) => {
  const [f, setF] = useState({ workDt: today(), dueDt: '', mgr: '', memo: '' });
  const [prev, setPrev] = useState<WorkDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    workDetail(estiNo).then(r => {
      setPrev(r.data);
      const e = r.data.estimate;
      if (e.ORDER_DT) setF({ workDt: d8(e.WORK_DT) || today(), dueDt: d8(e.DUE_DT), mgr: e.WORK_MGR || '', memo: e.WORK_MEMO || '' });
    }).catch(() => {});
  }, [estiNo]);
  const started = prev?.panels.filter(p => p.STATE !== 'TODO').length ?? 0;
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await issueOrder(estiNo, { items, ...f });
      onDone(r.data.kept ? `작업지시를 고쳤습니다 — ${r.data.total}장 (이미 진행된 ${r.data.kept}장은 그대로)` : `작업지시 완료 — 패널 ${r.data.total}장`);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div style={overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...box, maxWidth: 440 }}>
        <div style={head()}>
          <b>작업지시 — {estiNo}</b><button onClick={onClose} style={xBtn}>✕</button>
        </div>
        <div style={{ padding: '4px 24px 22px' }}>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 12, lineHeight: 1.6 }}>
            이 도면의 패널 <b>{items.length}장</b>을 번호(라벨과 같은 번호)별로 등록합니다.
            {prev?.estimate.ORDER_DT && <><br /><span style={{ color: '#F76707' }}>이미 작업지시한 견적입니다 — 도면이 바뀌었으면 생산 전 패널만 새 목록으로 바뀌고, 진행된 {started}장은 그대로 둡니다.</span></>}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ ...label, flex: 1 }}>작업일<input type="date" value={f.workDt} onChange={e => setF({ ...f, workDt: e.target.value })} style={input} /></label>
            <label style={{ ...label, flex: 1 }}>출고 예정일<input type="date" value={f.dueDt} onChange={e => setF({ ...f, dueDt: e.target.value })} style={input} /></label>
          </div>
          <label style={{ ...label, marginTop: 12 }}>담당<input value={f.mgr} onChange={e => setF({ ...f, mgr: e.target.value })} style={input} /></label>
          <label style={{ ...label, marginTop: 12 }}>메모<input value={f.memo} onChange={e => setF({ ...f, memo: e.target.value })} style={input} /></label>
          {err && <div style={{ color: '#F04452', fontSize: 13, marginTop: 10 }}>⚠ {err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button onClick={onClose} style={btn('#757575')}>취소</button>
            <button onClick={submit} disabled={busy} style={btn()}>
              {busy ? '처리 중…' : prev?.estimate.ORDER_DT ? '작업지시 고치기' : '작업지시'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** QR로 연 패널 화면 (폰) — ?p=견적번호:패널번호 */
export const PanelScan: React.FC<{ code: string; onClose: () => void; onBoard: (no: string) => void }> = ({ code, onClose, onBoard }) => {
  const [estiNo, no] = code.split(':');
  const [d, setD] = useState<WorkDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [loc, setLoc] = useState(() => { try { return localStorage.getItem('pd_last_loc') || ''; } catch { return ''; } });
  const load = () => workDetail(estiNo).then(r => { setD(r.data); setErr(null); }).catch(e => setErr(errMsg(e)));
  useEffect(() => { load(); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps
  const p = d?.panels.find(x => x.PANEL_NO === no);
  const act = async (action: 'made' | 'in' | 'done', label: string) => {
    try {
      await panelAction(estiNo, [no], action, action === 'in' ? loc : undefined);
      if (action === 'in') try { localStorage.setItem('pd_last_loc', loc); } catch { /* 저장소 없음 */ }
      setMsg(`✓ ${no} ${label}`); await load();
    } catch (e) { setErr(errMsg(e)); }
  };
  const big = (bg: string, on = true): React.CSSProperties => ({ width: '100%', padding: 16, fontSize: 18, fontWeight: 'bold', border: 'none', borderRadius: 14,
    background: on ? bg : '#e0e0e0', color: on ? '#fff' : '#999', cursor: on ? 'pointer' : 'default', marginTop: 10 });
  return (
    <div style={{ height: '100vh', overflowY: 'auto', background: '#F2F4F6', padding: 16, boxSizing: 'border-box', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
          {!d ? <div style={{ color: err ? '#F04452' : '#888' }}>{err ?? '불러오는 중…'}</div>
            : !p ? <div style={{ color: '#F04452' }}>이 견적에 {no} 패널이 없습니다 — 작업지시 전이거나 도면이 바뀌었습니다.</div>
            : <>
              <div style={{ fontSize: 13, color: '#777' }}>{d.estimate.SITE_NM || '(현장명 없음)'} · {estiNo}</div>
              <div style={{ fontSize: 44, fontWeight: 800, margin: '4px 0' }}>{p.PANEL_NO}</div>
              <div style={{ fontSize: 17, fontWeight: 'bold' }}>{p.KIND} {p.SIZE} × {p.LENGTH}</div>
              <div style={{ fontSize: 14, color: '#555', margin: '4px 0 10px' }}>{p.JOINT} · {d.estimate.PANEL_THICK}T · 견적 {jobOf(d.estimate.JOB_STATE).label}</div>
              <div style={{ display: 'inline-block', padding: '6px 14px', borderRadius: 20, background: PANEL[p.STATE].color, color: '#fff', fontWeight: 'bold' }}>
                {PANEL[p.STATE].label}{p.LOC ? ` · ${p.LOC}` : ''}</div>
              {msg && <div style={{ color: '#03B26C', fontWeight: 'bold', marginTop: 12 }}>{msg}</div>}
              {err && <div style={{ color: '#F04452', marginTop: 12 }}>⚠ {err}</div>}
              <button style={big('#F76707', p.STATE === 'TODO')} disabled={p.STATE !== 'TODO'} onClick={() => act('made', '생산 완료')}>생산 완료</button>
              <input value={loc} onChange={e => setLoc(e.target.value)} placeholder="보관 위치 (예: A-3)" style={{ ...input, marginTop: 14, fontSize: 16, padding: 12 }} />
              <button style={big('#1565C0', p.STATE === 'TODO' || p.STATE === 'MADE')} disabled={!(p.STATE === 'TODO' || p.STATE === 'MADE')} onClick={() => act('in', '입고')}>입고</button>
              <button style={big('#03B26C', p.STATE === 'OUT')} disabled={p.STATE !== 'OUT'} onClick={() => act('done', '설치 완료')}>설치 완료 (현장)</button>
              <div style={{ fontSize: 12, color: '#888', marginTop: 10 }}>출고(차에 싣기)는 작업 현황의 배차에서 합니다.</div>
            </>}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={() => onBoard(estiNo)} style={{ flex: 1, padding: 12, border: '1px solid #ccc', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>📋 이 견적 작업 현황</button>
          <button onClick={onClose} style={{ flex: 1, padding: 12, border: '1px solid #ccc', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>도면 화면으로</button>
        </div>
      </div>
    </div>
  );
};
